import { useEffect, useRef, useState } from "react";

interface Call { number: number; room: string | null; doctor: string; status: string; calledAt: string; callCount: number }
interface State { hospitalName: string; branchName: string; generatedAt: string; nowCalling: Call[]; upNext: { doctor: string; numbers: number[] }[] }

/** Waiting-room TV. No login: the link carries a revocable token, sent as a header. Numbers and rooms only. */
export function Display({ token }: { token: string }) {
  const [st, setSt] = useState<State | null>(null);
  const [offline, setOffline] = useState(false);
  const [dead, setDead] = useState(false);
  const [sound, setSound] = useState<"off" | "en" | "en+hi">("off");
  const [clock, setClock] = useState(new Date());
  const seen = useRef<Map<number, string>>(new Map());
  const first = useRef(true);
  const [fresh, setFresh] = useState<number | null>(null);

  useEffect(() => {
    let stop = false;
    const tick = async () => {
      try {
        const r = await fetch("/api/v1/display/state", { headers: { "x-display-token": token }, cache: "no-store" });
        if (r.status === 404) { setDead(true); return; }
        if (!r.ok) throw new Error(String(r.status));
        const s: State = await r.json();
        if (stop) return;
        setOffline(false); setSt(s);
        // Announce calls that are new or repeated since the last poll (not on first load).
        for (const c of s.nowCalling.filter((x) => x.status === "called")) {
          const key = `${c.calledAt}|${c.callCount}`;
          if (seen.current.get(c.number) !== key) { seen.current.set(c.number, key); if (!first.current) { setFresh(c.number); announce(c); } }
        }
        first.current = false;
      } catch { setOffline(true); }
    };
    tick();
    const t = setInterval(tick, 3000), c = setInterval(() => setClock(new Date()), 15_000);
    return () => { stop = true; clearInterval(t); clearInterval(c); };
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const soundRef = useRef(sound); soundRef.current = sound;
  function announce(c: Call) {
    if (soundRef.current === "off" || !("speechSynthesis" in window)) return;
    const say = (text: string, lang: string) => { const u = new SpeechSynthesisUtterance(text); u.lang = lang; u.rate = 0.9; speechSynthesis.speak(u); };
    say(`Token number ${c.number}${c.room ? `, please go to room ${c.room}` : `, ${c.doctor}`}.`, "en-IN");
    if (soundRef.current === "en+hi") say(`टोकन नंबर ${c.number}${c.room ? `, कृपया कमरा नंबर ${c.room} में जाएँ` : ""}।`, "hi-IN");
  }

  if (dead) return <div className="tv tv-dead"><p>This screen link has been switched off. Ask the hospital administrator for a new link.</p></div>;
  const time = clock.toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit" });
  return (
    <div className="tv">
      <header className="tv-top">
        <div><span className="tv-hospital">{st?.hospitalName ?? ""}</span>{st && st.branchName !== st.hospitalName && <span className="tv-branch">{st.branchName}</span>}</div>
        <div className="tv-right">
          {offline && <span className="tv-offline" role="status">Reconnecting…</span>}
          <span className="tv-clock">{time}</span>
          <select className="tv-sound" aria-label="Announcements" value={sound} onChange={(e) => setSound(e.target.value as typeof sound)}>
            <option value="off">Sound off</option><option value="en">Announce in English</option><option value="en+hi">English and Hindi</option>
          </select>
        </div>
      </header>
      <main className="tv-main">
        <h1 className="tv-heading">Now calling</h1>
        {st && !st.nowCalling.length && <p className="tv-empty">Please wait. Your token number will appear here.</p>}
        <ul className={`tv-calls${st && st.nowCalling.length <= 2 ? " few" : ""}`} aria-live="polite">
          {st?.nowCalling.map((c) => (
            <li key={`${c.number}-${c.calledAt}`} className={`tv-call${c.number === fresh ? " fresh" : ""}${c.status === "in_consultation" ? " inside" : ""}`}>
              <span className="tv-num" data-testid="tv-number">{c.number}</span>
              <span className="tv-arrow" aria-hidden="true">→</span>
              <span className="tv-dest"><span className="tv-room">{c.room ? `Room ${c.room}` : "Please come in"}</span><span className="tv-doc">{c.doctor}</span></span>
            </li>))}
        </ul>
      </main>
      {st && st.upNext.length > 0 && <footer className="tv-next">
        <span className="tv-next-label">Next</span>
        {st.upNext.map((n) => <span key={n.doctor} className="tv-next-group"><span className="tv-doc">{n.doctor}</span> {n.numbers.join("  ")}</span>)}
      </footer>}
    </div>
  );
}
